// Lightweight inline markup for COMMENTARY note bodies only (not liturgy text —
// ADR-006 keeps liturgy to the InlineRun bold/italic model). Notes are a plain
// string; users mark up with a tiny markdown subset:
//   **bold**   *italic*   _underline_
// Non-nesting, inline-only. This is the single source of truth: the web renders
// tokens to <strong>/<em>/<u>, and the worker strips them to plain text when
// handing commentary to the GAS Doc renderer (which has no markdown awareness).

export interface NoteMarkupToken {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

// `**` (bold) is tried before `*` (italic); inner content can't contain its own
// delimiter, so `**x**` matches the bold branch, never italic.
const PATTERN = /\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_/g;

/** Split a note body into styled tokens. Plain text → a single unstyled token. */
export function tokenizeNoteMarkup(body: string): NoteMarkupToken[] {
  const out: NoteMarkupToken[] = [];
  const re = new RegExp(PATTERN); // fresh instance — PATTERN carries /g state
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push({ text: body.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ text: m[1], bold: true });
    else if (m[2] !== undefined) out.push({ text: m[2], italic: true });
    else if (m[3] !== undefined) out.push({ text: m[3], underline: true });
    last = re.lastIndex;
  }
  if (last < body.length) out.push({ text: body.slice(last) });
  return out;
}

/** The note body with all markup delimiters removed (for plain-text consumers). */
export function stripNoteMarkup(body: string): string {
  return tokenizeNoteMarkup(body)
    .map((t) => t.text)
    .join('');
}
